// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  IconButton,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

/**
 * QualificationItemsStep Component (Step 3: Qualification Boolean)
 *
 * Defines qualification items with: Qualification Name, Qualification Question,
 * Qualification Answer (matching keywords), and Mandatory (Yes/No).
 * Reads/writes formData.categories[0].metrics; ensures one category exists.
 */
const QualificationItemsStep = ({
  formData,
  errors,
  onQualificationItemsChange
}) => {
  const categories = formData.categories || [];
  const singleCategory = categories[0];

  useEffect(() => {
    if (categories.length === 0) {
      onQualificationItemsChange([
        {
          category_name: 'Qualification',
          category_key: 'qualification',
          weight_percentage: 0,
          metrics: [],
          use_knowledge_for_analysis: false
        }
      ]);
    }
  }, []);

  const handleMetricsUpdate = (newMetrics) => {
    const base = categories.length > 0 ? categories[0] : {
      category_name: 'Qualification',
      category_key: 'qualification',
      weight_percentage: 0,
      metrics: [],
      use_knowledge_for_analysis: false
    };
    onQualificationItemsChange([{ ...base, metrics: newMetrics }]);
  };

  const handleAddItem = () => {
    const current = categories.length > 0 ? (singleCategory.metrics || []) : [];
    const newMetric = {
      metric_name: '',
      metric_key: `qual-${Date.now()}`,
      description: '',
      weight_percentage: 0,
      possible_scores: ['Yes', 'No', 'N/A'],
      keywords: [],
      evaluation_guide: '',
      is_mandatory: true,
      display_order: current.length
    };
    handleMetricsUpdate([...current, newMetric]);
  };

  const handleItemChange = (index, field, value) => {
    const current = categories.length > 0 ? (singleCategory.metrics || []) : [];
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'matching_answers_str') {
      const keywords = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
      updated[index].keywords = keywords;
    }
    if (field === 'mandatory_yes_no') {
      updated[index].is_mandatory = value === 'Yes';
    }
    handleMetricsUpdate(updated);
  };

  const handleDeleteItem = (index) => {
    const current = categories.length > 0 ? (singleCategory.metrics || []) : [];
    handleMetricsUpdate(current.filter((_, i) => i !== index));
  };

  const items = categories.length > 0 ? (singleCategory.metrics || []) : [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 3: Qualification Boolean
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define qualification items. For each item, specify the question, matching answers (Yes), and whether it is mandatory.
      </Typography>

      {errors.categories && typeof errors.categories === 'string' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.categories}
        </Alert>
      )}

      {items.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            No qualification items yet. Click &quot;Add qualification item&quot; to add Qualification Name, Question, Answer, and Mandatory.
          </Typography>
        </Alert>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {items.map((item, index) => (
            <Paper
              key={item.metric_key || index}
              variant="outlined"
              sx={{ p: 2 }}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Item #{index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteItem(index)}
                    aria-label="Delete item"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
                <TextField
                  label="Qualification Name"
                  placeholder="e.g. Relationship Status, Age"
                  value={item.metric_name || ''}
                  onChange={(e) => handleItemChange(index, 'metric_name', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Short name for display (e.g. Relationship Status, Age)"
                />
                <TextField
                  label="Qualification Question *"
                  placeholder="Question or instruction for the agent, including Pass if conditions"
                  value={item.description || ''}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  size="small"
                  helperText="Question or instruction for the agent, including Pass if conditions"
                />
                <TextField
                  label="Qualification Answer"
                  placeholder="e.g. married, 28, 40000, consent"
                  value={
                    item.matching_answers_str !== undefined && item.matching_answers_str !== null
                      ? item.matching_answers_str
                      : (Array.isArray(item.keywords) ? item.keywords.join(', ') : '')
                  }
                  onChange={(e) => handleItemChange(index, 'matching_answers_str', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Comma-separated phrases or keywords that mean Yes/qualified"
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Mandatory</InputLabel>
                  <Select
                    value={item.is_mandatory === false ? 'No' : 'Yes'}
                    onChange={(e) => handleItemChange(index, 'mandatory_yes_no', e.target.value)}
                    label="Mandatory"
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Whether this qualification item must pass for the customer to be qualified
                  </Typography>
                </FormControl>
                <TextField
                  label="Fail conditions / Evaluation notes (optional)"
                  placeholder="e.g. Single = not pass; only work income counts"
                  value={item.evaluation_guide || ''}
                  onChange={(e) => handleItemChange(index, 'evaluation_guide', e.target.value)}
                  fullWidth
                  multiline
                  minRows={1}
                  size="small"
                  helperText="Conditions that mean Fail, or special rules for evaluation"
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={handleAddItem}
        size="large"
        fullWidth
        sx={{ mb: 2 }}
      >
        Add qualification item
      </Button>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Tips:</strong>
          <br />
          • Qualification Question is the instruction the AI will use to check the conversation.
          <br />
          • Qualification Answer lists keywords/phrases that indicate the customer is qualified (Yes).
          <br />
          • Mandatory = Yes means this item must pass for the customer to be considered qualified.
        </Typography>
      </Alert>
    </Box>
  );
};

export default QualificationItemsStep;
